import React, { useState } from "react";
import { useMutation } from "react-query";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@emotion/react";
import { Box, Typography, Container, Card, Grid } from "@mui/material";

import LanguageIcon from "@mui/icons-material/Language";

import SelectButtonMenu from "./SelectButtonMenu";

import * as api from "../services";
import CustomButton from "./CustomButton";
import useAccountSettings from "../ManageAccount/useAccountSettings";
import { useContext } from "react";
import AppContext from "../appContext";
import { useEffect } from "react";

const languages = [
  { id: "nl", title: "Nederlands" },
  { id: "en", title: "English" },
  { id: "de", title: "German" },
];

const translationObject = {
  nl: {
    subtitle: "Wilt u m-Path verbeteren met uw ervaringen? ",
    description:
      "Ons team wilt dat u de best mogelijke ervaring heeft op het platform. Mogen we u sporadisch contacteren via e-mail om deel te nemen aan onderzoek over de gebruikerservaring van m-Path? Indien u niet geïntresseerd bent, kan u deze berichten steeds negeren. U kan uw voorkeur altijd wijzigen via instellingen om geen berichten meer te ontvangen.",
    joinResearch: "Ja, contacteer me vrijblijvend",
    skip: "Neen, stuur me geen berichten",
  },
  en: {
    subtitle: "Do you want to improve m-Path with your experiences? ",
    description:
      "Our team wants you to have the best possible experience on the platform. May we sporadically invite you via email to participate in user-experience research about m-Path? You can ignore research requests you are not interested in. You can always stop getting these requests by changing your preferences in settings.",
    joinResearch: "Yes, keep me informed",
    skip: "No, don't send me messages",
  },
   de: {
    subtitle: "Möchten Sie m-Path mit Ihren Erfahrungen verbessern?",
    description:
      "Unser Team möchte, dass Sie die bestmögliche Erfahrung auf der Plattform haben. Dürfen wir Sie sporadisch per E-Mail einladen, an der Benutzererfahrungsforschung zu m-Path teilzunehmen? Sie können Forschungsanfragen ignorieren, die Sie nicht interessieren. Sie können jederzeit aufhören, diese Anfragen zu erhalten, indem Sie Ihre Einstellungen ändern.",
    joinResearch: "Ja, informieren Sie mich",
    skip: "Nein, senden Sie mir keine Nachrichten",
  },
};

export default function JoinResearch({ onClose }) {
  const {
    app: { sessionId },
  } = useContext(AppContext);
  const { accountSettings, refetchSettings } = useAccountSettings();
  const mutation = useMutation(
    (mailOnResearch) =>
      api.postUserSettings({
        ...accountSettings,
        mailOnResearch: mailOnResearch,
        sessionId,
      }),
    {
      onSuccess: (data) => {
        if (data.data?.status === 1) {
          onClose();
          refetchSettings();
        }
      },
    }
  );
  const handleClose = (mailOnResearch) => {
    mutation.mutate(mailOnResearch);
  };

  const [lang, setLang] = useState(accountSettings?.lang ?? "en");
  const theme = useTheme();
  const { subtitle, description, changeLang, joinResearch, skip } =
    translationObject?.[lang] ?? translationObject["en"];
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (accountSettings?.lang) {
      setLang(accountSettings.lang);
    }
  }, [accountSettings]);

  return (
    <Box>
      <Container maxWidth="xl">
        <Box padding={1} />
        {/** Brand and language selector */}
        <Box display={"flex"} justifyContent="space-between">
          <img
            alt="Logo m-Path KULeuven"
            src={`${window.REACT_APP_IMAGE_PATH}/logo-horizontal.png`}
            height={50}
            sx={{ opacity: 1 }}
          />
          <SelectButtonMenu
            buttonText={lang}
            buttonTooltip={changeLang}
            options={languages.map((l) => l.id)}
            titles={languages.map((l) => l.title)}
            handleOptionClick={(index) => setLang(languages[index].id)}
            startButtonIcon={<LanguageIcon />}
          />
        </Box>
        <Box padding={1} />
        {/** Landing section */}
        <Card
          sx={{
            backgroundColor: "rgb(1, 19, 54)",
            color: "#fff",
            borderRadius: "24px",
          }}
        >
          <Container>
            <Box padding={2} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box padding={3} />
                <Typography variant="h4">{subtitle}</Typography>
                <Box padding={2} />
                <Typography variant="body1">{description}</Typography>
                <Box padding={2} />
                <CustomButton
                  dark={true}
                  text={joinResearch}
                  props={{ onClick: () => handleClose(1) }}
                />
                <Box padding={2} />
                <CustomButton
                  dark={true}
                  text={skip}
                  props={{ onClick: () => handleClose(0) }}
                />
              </Grid>
              <Grid item xs={12} md={6} textAlign="end">
                {/** Hide image on mobile devices */}
                {!isMobile ? (
                  <img
                    alt="smartphone"
                    src={`${window.REACT_APP_IMAGE_PATH}/phones-${lang}.png`}
                    height={480}
                  />
                ) : null}
              </Grid>
            </Grid>
            <Box padding={3} />
          </Container>
        </Card>
      </Container>
    </Box>
  );
}
